import React, { useMemo, useState } from "react";
import { ConfigProvider, Input, Menu, Typography, Flex } from "antd";
import {
  RightOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";

const roleMenuItem = (role) => ({
  key: role.id,
  label: (
    <Flex align="center" gap={6} style={{ width: "100%", minWidth: 0 }}>
      <UserOutlined style={{ color: "#667085", flexShrink: 0 }} />
      <Typography.Text
        ellipsis={{ tooltip: { title: role.name, placement: "top" } }}
        style={{ flex: 1, minWidth: 0 }}
      >
        {role.name}
      </Typography.Text>
    </Flex>
  ),
});

const RolesSidebar = ({ roles, activeId, onSelect }) => {
  const [query, setQuery] = useState("");
  const [builtinsOpen, setBuiltinsOpen] = useState(false);

  const { customRoles, systemRoles } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (r) => !q || r.name.toLowerCase().includes(q);
    return {
      customRoles: roles.filter((r) => !r.system && matches(r)),
      systemRoles: roles.filter((r) => r.system && matches(r)),
    };
  }, [roles, query]);

  const activeIsBuiltin = !!roles.find((r) => r.id === activeId)?.system;

  // Auto-expand built-ins when: the active role is a built-in (so the user
  // can see what's selected on refresh), there's an active search, or
  // there are no custom roles to fall back on.
  const showBuiltins =
    builtinsOpen ||
    activeIsBuiltin ||
    query.trim().length > 0 ||
    customRoles.length === 0;

  const renderMenu = (items) => (
    <Menu
      mode="inline"
      inlineIndent={8}
      selectedKeys={activeId ? [activeId] : []}
      onClick={(info) => onSelect(info.key)}
      style={{ border: "none", background: "transparent" }}
      items={items}
    />
  );

  return (
    <Flex vertical gap={12}>
      <Typography.Text
        type="secondary"
        style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em" }}
      >
        CLINICAL ROLES
      </Typography.Text>
      <Input
        placeholder="Search roles..."
        prefix={<SearchOutlined style={{ color: "#98a2b3" }} />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        allowClear
      />
      <ConfigProvider theme={{ components: { Menu: { itemPaddingInline: 8 } } }}>
        {customRoles.length > 0 && renderMenu(customRoles.map(roleMenuItem))}

        {systemRoles.length > 0 && (
          <div>
            <button
              type="button"
              aria-expanded={showBuiltins}
              onClick={() => setBuiltinsOpen((v) => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                width: "100%",
                padding: "6px 8px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "#667085",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              <RightOutlined
                style={{
                  fontSize: 10,
                  transition: "transform 0.15s",
                  transform: showBuiltins ? "rotate(90deg)" : "none",
                }}
              />
              <span style={{ flex: 1, textAlign: "left" }}>Built-in roles</span>
              <span style={{ fontWeight: 500, color: "#98a2b3" }}>
                {systemRoles.length}
              </span>
            </button>
            {showBuiltins && renderMenu(systemRoles.map(roleMenuItem))}
          </div>
        )}

        {customRoles.length === 0 && systemRoles.length === 0 && (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, padding: "8px 4px" }}
          >
            No roles match "{query}"
          </Typography.Text>
        )}
      </ConfigProvider>
    </Flex>
  );
};

export default RolesSidebar;
